import React, { useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Car, Route, Users, Award, TrendingUp, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

export const StatsSection: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [vehicles, setVehicles] = useState(0);
  const [trips, setTrips] = useState(0);
  const [people, setPeople] = useState(0);

  const stats = [
    {
      icon: Car,
      value: 32002,
      label: t("1143", "Special Vehicles"),
      setter: setVehicles,
      current: vehicles,
      gradient: "from-primary to-primary-dark",
      description: t("1144", "Premium fleet ready"),
    },
    {
      icon: Route,
      value: 16008,
      label: t("1145", "Road Trips Completed"),
      setter: setTrips,
      current: trips,
      gradient: "from-primary-light to-primary",
      description: t("1146", "Successful journeys"),
    },
    {
      icon: Users,
      value: 17068,
      label: t("1147", "People Transported"),
      setter: setPeople,
      current: people,
      gradient: "from-warning to-primary",
      description: t("1148", "Happy customers"),
    },
  ];

  const achievements = [
    {
      icon: Award,
      title: t("1149", "5-Star Rating"),
      description: t("1150", "Customer satisfaction"),
    },
    {
      icon: Shield,
      title: t("1151", "100% Insured"),
      description: t("1152", "Complete protection"),
    },
    {
      icon: TrendingUp,
      title: t("1153", "24/7 Service"),
      description: t("1154", "Always available"),
    },
  ];

  useEffect(() => {
    if (isInView) {
      stats.forEach((stat, index) => {
        const timer = setTimeout(() => {
          const duration = 2500;
          const steps = 60;
          const increment = stat.value / steps;
          let current = 0;

          const counter = setInterval(() => {
            current += increment;
            if (current >= stat.value) {
              current = stat.value;
              clearInterval(counter);
            }
            stat.setter(Math.floor(current));
          }, duration / steps);
        }, index * 300);

        return () => clearTimeout(timer);
      });
    }
  }, [isInView]);

  return (
    <section ref={ref} className="py-32 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Animated Particles */}
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        {/* Gradient Orbs */}
        <motion.div
          className="absolute top-20 left-20 w-96 h-96 bg-linear-to-r from-primary/10 to-primary-light/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-80 h-80 bg-linear-to-r from-warning/10 to-primary/10 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{ duration: 0.8 }}
            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-xs border border-white/20 text-white px-6 py-3 rounded-full text-sm font-medium mb-6"
          >
            <TrendingUp className="h-4 w-4" />
            <span>{t("1155", "Our Track Record")}</span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
            {t("1156", "Numbers That")}{" "}
            <span className="bg-linear-to-r from-warning to-primary bg-clip-text text-transparent">
              {t("1157", "Speak")}
            </span>
          </h2>

          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            {t(
              "1158",
              "Our commitment to excellence is reflected in every journey we complete"
            )}
          </p>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.8 }}
            className="w-32 h-1 bg-linear-to-r from-warning to-primary mx-auto rounded-full mt-8"
          />
        </motion.div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={
                isInView
                  ? { opacity: 1, y: 0, scale: 1 }
                  : { opacity: 0, y: 50, scale: 0.8 }
              }
              transition={{ duration: 0.8 }}
              whileHover={{ scale: 1.05, y: -10 }}
              className="group relative"
            >
              {/* Card Background */}
              <div className="relative bg-white/10 backdrop-blur-xs border border-white/20 rounded-3xl p-8 text-center text-white overflow-hidden">
                {/* Animated Background */}
                <motion.div
                  className={`absolute inset-0 bg-linear-to-r ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                />

                {/* Icon */}
                <motion.div
                  className={`inline-flex items-center justify-center w-20 h-20 bg-linear-to-r ${stat.gradient} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <stat.icon className="h-10 w-10 text-white" />
                </motion.div>

                {/* Number */}
                <motion.div
                  className="text-5xl md:text-6xl font-bold mb-2"
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : { scale: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {stat.current.toLocaleString()}
                </motion.div>

                {/* Label */}
                <div className="text-lg font-semibold mb-2">{stat.label}</div>

                {/* Description */}
                <div className="text-blue-100 text-sm">{stat.description}</div>

                {/* Decorative Elements */}
                <motion.div
                  className="absolute top-4 right-4 w-2 h-2 bg-white/30 rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.5,
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Achievement Badges */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {achievements.map((achievement) => (
            <motion.div
              key={achievement.title}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={
                isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
              }
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-4 bg-white/5 backdrop-blur-xs border border-white/10 rounded-2xl p-6 text-white"
            >
              <div className="w-12 h-12 bg-linear-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shrink-0">
                <achievement.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="font-bold text-lg">{achievement.title}</div>
                <div className="text-blue-100 text-sm">
                  {achievement.description}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
